<template>
  <section :class="{ 'lg:mt-20': index > 0 }">
    <div class="flex flex-col lg:flex-row gap-y-[30px] lg:gap-x-[150px] 2xl:gap-x-[200px] lg:w-2/2">
      <!-- Text Content (for right-aligned image) -->
      <div v-if="orientation === 'right'" class="hidden lg:w-1/2 relative lg:flex flex-col xl:mr-[5rem]">
        <div class="flex flex-wrap lg:justify-start gap-[10px] lg:gap-[20px] mt-4">
          <CaseStudyTopicButton
              v-for="topic in study.topics"
              :key="topic"
              :topic="topic"
              :is-active="activeFilters.includes(topic)"
          />
        </div>
        <div class="flex flex-col lg:gap-y-[50px] 2xl:gap-y-[50px] mt-[28px] lg:mt-0 h-full justify-center">
          <p class="text-[12px] lg:text-[18px] xl:text-[24px] 2xl:text-[32px] font-[500] leading-[21px] lg:leading-[32px] 2xl:leading-[42px] w-2/3 lg:w-full lg:mt-4 2xl:mt-0">
            {{ study.description }}
          </p>
          <div class="flex w-full justify-end lg:justify-start">
            <button class="cta-button mt-[10px] lg:mt-0" style="z-index: 120;" @click="goToDetails(study.slug)">
              <span class="text text-[12px] lg:text-[24px]">See More</span>
              <svg class="arrow hidden lg:block" fill="none" height="19" viewBox="0 0 35 19" width="35" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 9.27539H33.0682" stroke="#52DA75" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
                <path d="M25.8932 1L32.2859 7.39262C33.328 8.4347 33.328 10.1277 32.2859 11.1698L25.8932 17.5624" stroke="#52DA75" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
              </svg>
              <svg class="lg:hidden" fill="none" height="14" viewBox="0 0 25 14" width="25" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 7.27686H24" stroke="#52DA75" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M18.854 1.3418L23.4389 5.92672C24.1863 6.67413 24.1863 7.8884 23.4389 8.63581L18.854 13.2207" stroke="#52DA75" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Image Content -->
      <div class="lg:w-1/2 relative" :class="{
        'lg:first:flex lg:first:items-center': orientation === 'left',
        '[&:nth-child(2)]:lg:flex [&:nth-child(2)]:justify-end [&:nth-child(2)]:lg:items-center': orientation === 'right'
      }">
        <div class="flex items-center" :class="{ 'justify-start': orientation === 'left', 'justify-end': orientation === 'right' }">
          <div class="relative w-2/3 lg:w-full">
            <img class="w-full object-fill h-full" :src="study.image" alt="case study image">
            <div v-if="study.brandImage.src" :class="study.brandImage.classes">
              <img class="hidden lg:block" width="127px" height="72px" :src="study.brandImage.src" alt="brand">
              <img class="lg:hidden" width="96px" height="54px" :src="study.brandImage.src" alt="brand">
            </div>
          </div>
        </div>
        <template v-for="svg in processedSvgs" :key="svg.classes">
          <div :class="svg.classes">
            <img :width="svg.width" :height="svg.height" :src="svg.src" :style="svg.style" alt="decorative element">
          </div>
        </template>
      </div>

      <!-- Text Content (for left-aligned image or mobile) -->
      <div class="lg:w-1/2 relative flex flex-col" :class="{ 'xl:mx-[5rem]': orientation === 'left', 'lg:hidden': orientation === 'right' }">
        <div class="flex flex-wrap gap-[10px] lg:gap-[20px] mt-4" :class="{ 'lg:justify-end': orientation === 'left', 'lg:justify-start': orientation === 'right' }">
          <CaseStudyTopicButton
              v-for="topic in study.topics"
              :key="topic"
              :topic="topic"
              :is-active="activeFilters.includes(topic)"
          />
        </div>
        <div class="flex flex-col lg:gap-y-[50px] 2xl:gap-y-[50px] mt-[28px] lg:mt-0 h-full justify-center">
          <p class="text-[12px] lg:text-[18px] xl:text-[24px] 2xl:text-[32px] font-[500] leading-[21px] lg:leading-[32px] 2xl:leading-[42px] w-2/3 lg:w-full lg:mt-4 2xl:mt-0">
            {{ study.description }}
          </p>
          <div class="flex w-full justify-end" :class="{
            'justify-start': orientation === 'left',
            'lg:justify-start': true,
          }">
            <button class="cta-button mt-[10px] lg:mt-0" style="z-index: 120;" @click="goToDetails(study.slug)">
              <span class="text text-[12px] lg:text-[24px]">See More</span>
              <svg class="arrow hidden lg:block" fill="none" height="19" viewBox="0 0 35 19" width="35" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 9.27539H33.0682" stroke="#52DA75" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
                <path d="M25.8932 1L32.2859 7.39262C33.328 8.4347 33.328 10.1277 32.2859 11.1698L25.8932 17.5624" stroke="#52DA75" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
              </svg>
              <svg class="lg:hidden" fill="none" height="14" viewBox="0 0 25 14" width="25" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 7.27686H24" stroke="#52DA75" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M18.854 1.3418L23.4389 5.92672C24.1863 6.67413 24.1863 7.8884 23.4389 8.63581L18.854 13.2207" stroke="#52DA75" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router';
import { computed } from 'vue';
import CaseStudyTopicButton from '../CaseStudiesComponents/CaseStudyTopicButton.vue';

const props = defineProps({
  study: {
    type: Object,
    required: true
  },
  orientation: {
    type: String,
    required: true,
    validator: (value: string) => ['left', 'right'].includes(value)
  },
  activeFilters: {
    type: Array as () => string[],
    required: true
  },
  index: {
    type: Number,
    required: true
  }
});

const processedSvgs = computed(() => {
  const initialOrientation = props.study.initialIndex % 2 === 0 ? 'left' : 'right';
  return props.study.svgs.map((svg: any) => {
    let { classes } = svg;
    if (initialOrientation !== props.orientation) {
      const classList = classes.split(' ');

      const tempClassList = classList.map((cls: string) => {
        if (cls === '-left-0') return 'TEMP-0_SPECIAL';
        if (cls.startsWith('left-')) return cls.replace('left-', 'TEMP-');
        if (cls.startsWith('-left-')) return cls.replace('-left-', '-TEMP-');
        return cls;
      });

      const leftClassList = tempClassList.map((cls: string) => {
        if (cls === '-right-0') return 'left-0';
        if (cls.startsWith('right-')) return cls.replace('right-', 'left-');
        if (cls.startsWith('-right-')) return cls.replace('-right-', '-left-');
        return cls;
      });

      const finalClassList = leftClassList.map((cls: string) => {
        if (cls === 'TEMP-0_SPECIAL') return 'right-0';
        if (cls.startsWith('TEMP-')) return cls.replace('TEMP-', 'right-');
        if (cls.startsWith('-TEMP-')) return cls.replace('-TEMP-', '-right-');
        return cls;
      });

      // Swap translate-x classes
      const txIndex = finalClassList.indexOf('translate-x-1/2');
      const negTxIndex = finalClassList.indexOf('-translate-x-1/2');

      if (txIndex !== -1) {
        finalClassList[txIndex] = '-translate-x-1/2';
      } else if (negTxIndex !== -1) {
        finalClassList[negTxIndex] = 'translate-x-1/2';
      }
      classes = finalClassList.join(' ');
    }
    return { ...svg, classes };
  });
});

const router = useRouter();

const goToDetails = (slug: string) => {
  router.push(`/case-studies/${slug}`);
};
</script>

<style scoped>
</style> 